'use client'

import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePopper } from 'react-popper'
import { useEvent } from 'react-use'

type PopperPortalProps = {
  active?: boolean
  children: ReactNode
}

const POPPER_OPTIONS: Parameters<typeof usePopper>[2] = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [8, 8]
      }
    }
  ],
  placement: 'right-start'
}

const useVirtualReference = () => {
  const refRect = useRef({
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    toJSON: () => this,
    top: 0,
    width: 0,
    x: 0,
    y: 0
  })

  const virtualReference = useMemo(
    () => ({
      getBoundingClientRect: () => refRect.current,
      update: (x: number, y: number) => {
        refRect.current = {
          bottom: y,
          height: 0,
          left: x,
          right: x,
          toJSON: () => this,
          top: y,
          width: 0,
          x,
          y
        }
      }
    }),
    [refRect.current]
  )

  return virtualReference
}

export const PopperPortal = ({ children }: PopperPortalProps) => {
  const [portalElement, setPortalElement] = useState<HTMLDivElement>()
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>()
  const virtualReference = useVirtualReference()

  const { attributes, styles, update } = usePopper(virtualReference, popperElement, POPPER_OPTIONS)

  useEffect(() => {
    const el = document.createElement('div')
    document.body.appendChild(el)
    setPortalElement(el)
    return () => el.remove()
  }, [])

  useEvent('mousemove', ({ clientX: x, clientY: y }) => {
    virtualReference.update(x, y)
    update?.()
  })

  useEffect(() => {
    update?.()
  }, [update])

  if (!portalElement) {
    return null
  }

  return createPortal(
    <div
      ref={setPopperElement}
      {...attributes.popper}
      style={{
        ...styles.popper,
        zIndex: 1000
      }}
    >
      {children}
    </div>,
    portalElement
  )
}
